// Morrow — minimal composer for a WORKFLOW RUN thread. A run is driven server-side, so
// there is nothing to type: while the run is in progress we show ONLY a Stop button; once
// it settles we show a short status line. No text input, model picker, or attachments.
// Rendered in place of <ChatComposer> for workflow-run threads (ChatView swaps on
// isWorkflowRun); the normal composer path is untouched.
import { CheckIcon, Loader2Icon, MinusIcon, XIcon } from "lucide-react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";

/** The red Stop button — markup mirrors ComposerPrimaryActions' stop-generation button. */
function StopRunButton({
  isInterrupting,
  onInterrupt,
}: {
  isInterrupting: boolean;
  onInterrupt: () => void;
}) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      disabled={isInterrupting}
      className={cn(
        "flex items-center justify-center rounded-full bg-destructive/90 text-white shadow-xs shadow-destructive/24 inset-shadow-[0_1px_--theme(--color-white/16%)] transition-all duration-150",
        isInterrupting
          ? "cursor-default opacity-70"
          : "cursor-pointer hover:bg-destructive hover:scale-105 active:inset-shadow-[0_1px_--theme(--color-black/8%)] active:shadow-none",
        "size-8",
      )}
      onClick={isInterrupting ? undefined : onInterrupt}
      aria-label={t("workflowRun.stop")}
    >
      {isInterrupting ? (
        <svg width="14" height="14" viewBox="0 0 24 24" className="animate-spin" aria-hidden="true">
          <circle
            cx="12"
            cy="12"
            r="9"
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.3"
            strokeWidth="3"
          />
          <path
            d="M21 12a9 9 0 0 0-9-9"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
          <rect x="2" y="2" width="8" height="8" rx="1.5" />
        </svg>
      )}
    </button>
  );
}

function FinishedStatus({ status }: { status: string }) {
  const { t } = useI18n();
  const label =
    status === "ok"
      ? t("workflowRun.statusOk")
      : status === "failed"
        ? t("workflowRun.statusFailed")
        : status === "skipped"
          ? t("workflowRun.statusSkipped")
          : t("workflowRun.finished");
  const Icon =
    status === "ok"
      ? CheckIcon
      : status === "failed"
        ? XIcon
        : status === "skipped"
          ? MinusIcon
          : CheckIcon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-sm font-medium",
        status === "failed" ? "text-red-600 dark:text-red-400" : "text-muted-foreground",
      )}
    >
      <Icon
        className={cn(
          "size-4 shrink-0",
          status === "ok" && "text-emerald-500",
          status === "failed" && "text-red-500",
        )}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

/**
 * Minimal workflow-run composer. `isRunning` = the run's turn is in flight (show Stop);
 * otherwise show the settled status line. `status` is the run's status for that line.
 */
export function WorkflowRunComposer({
  isRunning,
  isInterrupting,
  status,
  onInterrupt,
}: {
  isRunning: boolean;
  isInterrupting: boolean;
  status: string;
  onInterrupt: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="flex w-full items-center justify-between gap-3 rounded-2xl border border-border/60 bg-background/80 px-4 py-3 shadow-sm">
      {isRunning ? (
        <>
          <span className="inline-flex min-w-0 items-center gap-2 truncate text-sm text-muted-foreground">
            <Loader2Icon className="size-4 shrink-0 animate-spin text-primary" aria-hidden="true" />
            <span className="truncate">{t("workflowRun.statusRunning")}</span>
          </span>
          <StopRunButton isInterrupting={isInterrupting} onInterrupt={onInterrupt} />
        </>
      ) : (
        <FinishedStatus status={status} />
      )}
    </div>
  );
}
